import { forwardRef, useMemo } from "react";
import { default as ReactSelect, GroupBase, SelectComponentsConfig, SelectInstance, StylesConfig } from "react-select";
import { RefType } from "../../../../types";
import { genericMemo } from "../../../../utils";
import { InputContainer } from "../../InputContainer";
import { ClearIndicator, DropdownIndicator, getStyles, Menu, MenuList, MenuPortal, MultiValue, Option, Placeholder } from "./components";
import { PartialBaseSelectProps, SelectComponentType, SelectProps } from "./types";

export const Select = genericMemo(forwardRef(<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
  T extends PartialBaseSelectProps<Option, IsMulti, Group> = PartialBaseSelectProps<Option, IsMulti, Group>,
  R extends SelectInstance<Option, IsMulti, Group> = SelectInstance<Option, IsMulti, Group>
>(
  props: SelectProps<Option, IsMulti, Group, T, R>,
  ref: RefType<R>,
) => {
  const selectStyles: StylesConfig<Option, IsMulti, Group> = useMemo(
    () => getStyles<Option, IsMulti, Group>(props.styles || {}, props.stylesMergePolicy ?? "merge"),
    [ props.styles, props.stylesMergePolicy ],
  );

  const selectComponents: SelectComponentsConfig<Option, IsMulti, Group> = useMemo(() => ({
    IndicatorSeparator: null,
    Placeholder,
    ClearIndicator,
    Option,
    Menu,
    MenuList,
    MenuPortal,
    MultiValue,
    ...props.components,
    DropdownIndicator: props.hideDropDownIcon ? null : props.components?.DropdownIndicator ?? DropdownIndicator,
  }), [ props.components, props.hideDropDownIcon ]);

  const wrappedOptions = useMemo(
    () => !props.predefinedOptions?.length
      ? props.options
      : [ ...props.predefinedOptions, ...(props.options || []) ],
    [ props.predefinedOptions, props.options ],
  );

  return (
    <InputContainer<SelectProps<Option, IsMulti, Group, T, R>, R>
      mapPropsToInput={({ allProps, inputProps: { selectComponent, ...inputProps } }) => ({
        ...inputProps,
        maxMenuHeight: inputProps.maxMenuHeight ?? 320,
        id: inputProps.id ? inputProps.id + "-container" : undefined,
        inputId: inputProps.id ? inputProps.id : undefined,
        isDisabled: allProps.disabled,
        customMenuShouldBlockScroll: props.menuShouldBlockScroll ?? true,
        menuPortalTarget: document.body,
        isClearable: props.isClearable,
        //remove custom options from base select
        predefinedOptions: undefined,
        hideDropDownIcon: undefined,
      }) as SelectProps<Option, IsMulti, Group, T, R>}
      inputComponent={props.selectComponent as any ?? ReactSelect}
      placeholder="Choose..."
      {...props}
      rootContainerProps={{
        "data-input-type": "select",
        ...props.rootContainerProps,
      }}
      options={wrappedOptions}
      value={props.value === undefined ? (props.isMulti ? [] : null) : props.value}
      styles={selectStyles}
      components={selectComponents}
      //menuShouldBlockScroll should be always false to avoid ScrollManager render
      menuShouldBlockScroll={false}
      isClearable={false}
      ref={ref}
    />
  );
}) as SelectComponentType);

